import { getLeadSource } from '../lib/leadSource';
import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { api } from '../lib/api';
import { useRegion } from '../contexts/RegionContext';
import { useLang } from '../contexts/LanguageContext';
import { SERVICES_KEYWORDS } from '../mock/serviceKeywords';

const PROJECT_TYPES = [
  { val: 'small',          label: 'Small / Personal',     icon: 'Home' },
  { val: 'residential',    label: 'Residential',          icon: 'Building' },
  { val: 'commercial',     label: 'Commercial',           icon: 'Building2' },
  { val: 'industrial',     label: 'Industrial / Plant',   icon: 'Factory' },
  { val: 'infrastructure', label: 'Infrastructure',       icon: 'TrafficCone' },
  { val: 'mixed-use',      label: 'Mixed-Use',            icon: 'Layers' }
];
const BUDGET_RANGES    = ['Under $5k', '$5k – $15k', '$15k – $50k', '$50k – $150k', '$150k – $500k', '$500k+', 'Not sure yet'];
const HOUR_RANGES      = ['< 40 hrs', '40 – 100 hrs', '100 – 250 hrs', '250 – 500 hrs', '500 – 1000 hrs', '1000+ hrs', 'Estimate for me'];
const TIMELINES        = ['Rush (< 1 week)', '1 – 2 weeks', '2 – 4 weeks', '1 – 3 months', '3 – 6 months', '6+ months', 'Ongoing retainer'];
const REVIEW_ROUNDS    = ['1 round', '2 rounds', '3 rounds', 'Unlimited (retainer)'];
const DELIVERY_FORMATS = [
  { val: 'cad-dwg',  label: 'CAD / DWG' },
  { val: 'pdf',      label: 'PDF set' },
  { val: 'bim-rvt',  label: 'BIM / RVT' },
  { val: '3d-model', label: '3D Model (FBX / OBJ)' },
  { val: 'report',   label: 'Engineering Report' },
  { val: 'gis',      label: 'GIS / Shapefile' }
];
const CONTACT_PREFS = [
  { val: 'email',    label: 'Email',      icon: 'Mail' },
  { val: 'phone',    label: 'Phone',      icon: 'Phone' },
  { val: 'whatsapp', label: 'WhatsApp',   icon: 'MessageCircle' },
  { val: 'video',    label: 'Video Call', icon: 'Video' }
];

const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

/* -------------------------- Small shared sub-components -------------------------- */

const PillGroup = ({ label, options, value, onChange, testidPrefix }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-green-400 font-bold">{label}</label>
    <div className="mt-2 grid grid-cols-1 gap-1.5">
      {options.map(o => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`text-left px-3 py-2 rounded text-xs border transition-colors ${value === o ? 'bg-gray-900/40 border-gray-500 text-green-200' : 'border-gray-900/40 text-gray-400 hover:border-green-700'}`}
          data-testid={`${testidPrefix}-${o.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const ProgressBar = ({ step, setStep }) => (
  <div className="flex items-center justify-between mb-8">
    {[
      { n: 1, label: 'Services' },
      { n: 2, label: 'Scope & Budget' },
      { n: 3, label: 'Delivery' },
      { n: 4, label: 'Your Info' }
    ].map((s, i) => (
      <React.Fragment key={s.n}>
        <button onClick={() => s.n < step && setStep(s.n)} disabled={s.n > step} className="flex items-center gap-2 group disabled:cursor-not-allowed" data-testid={`quote-step-${s.n}`}>
          <span className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm transition-all ${step >= s.n ? 'bg-orange-500 text-white' : 'bg-[#1a1f2e] border border-gray-900/50 text-gray-500'}`}>
            {step > s.n ? <Icons.Check className="w-4 h-4" /> : s.n}
          </span>
          <span className={`hidden sm:inline text-[11px] uppercase tracking-wider font-bold ${step >= s.n ? 'text-green-400' : 'text-gray-500'}`}>{s.label}</span>
        </button>
        {i < 3 && <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-gray-500' : 'bg-gray-900/40'}`} />}
      </React.Fragment>
    ))}
  </div>
);

/* -------------------------- Step 1: Services -------------------------- */

const SubServiceChip = ({ id, label, active, onClick }) => (
  <button
    key={id} type="button" onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${active ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-900/50 text-gray-300 hover:border-green-500'}`}
  >
    <span className={`w-3 h-3 rounded-sm border ${active ? 'bg-orange-500 border-orange-500' : 'border-gray-500'} flex items-center justify-center`}>
      {active && <Icons.Check className="w-2 h-2 text-white" />}
    </span>
    {label}
  </button>
);

const Step1Services = ({ services, setServices, subs, setSubs, setStep }) => {
  const selectedServiceObjs = SERVICES_KEYWORDS.filter(s => services.includes(s.slug));
  const canNext = services.length > 0;
  return (
    <div data-testid="quote-step-1-content">
      <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 1 of 4</div>
      <h3 className="text-2xl md:text-3xl font-black">What services do you need? <span className="text-orange-400">*</span></h3>
      <p className="text-gray-400 text-sm mt-2">Pick all that apply. We&apos;ll group sub-services in the next step.</p>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-2">
        {SERVICES_KEYWORDS.map(s => {
          const active = services.includes(s.slug);
          return (
            <button key={s.slug} type="button" onClick={() => toggle(services, setServices, s.slug)}
              className={`text-left p-4 rounded border-2 transition-all ${active ? 'bg-gray-900/30 border-gray-500' : 'bg-[#1a1f2e] border-gray-900/40 hover:border-green-700'}`}
              data-testid={`quote-svc-${s.slug}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-sm">{s.name}</div>
                <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 ${active ? 'bg-orange-500 border-orange-500' : 'border-gray-600'}`}>
                  {active && <Icons.Check className="w-3 h-3 text-white" />}
                </div>
              </div>
              <div className="text-[11px] text-gray-400 mt-1.5">{s.subServices.length} sub-services</div>
            </button>
          );
        })}
      </div>

      {selectedServiceObjs.length > 0 && (
        <div className="mt-7">
          <div className="text-orange-400 text-[10px] uppercase tracking-[0.25em] font-bold mb-3 flex items-center gap-2">
            <Icons.Layers className="w-3.5 h-3.5" /> Pick sub-services <span className="text-gray-500 font-normal">(optional)</span>
          </div>
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {selectedServiceObjs.map(s => (
              <div key={s.slug} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-3">
                <div className="text-green-400 text-[11px] uppercase tracking-wider font-bold mb-2">{s.name}</div>
                <div className="flex flex-wrap gap-2">
                  {s.subServices.map(sub => {
                    const id = `${s.slug}::${sub}`;
                    return <SubServiceChip key={id} id={id} label={sub} active={subs.includes(id)} onClick={() => toggle(subs, setSubs, id)} />;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        <div className="text-xs text-gray-400">
          <span className="text-green-400 font-bold">{services.length}</span> service{services.length !== 1 && 's'} · <span className="text-orange-400 font-bold">{subs.length}</span> sub
        </div>
        <button onClick={() => setStep(2)} disabled={!canNext} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider" data-testid="quote-next-1">
          Next <Icons.ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

/* -------------------------- Step 2: Scope & Budget -------------------------- */

const Step2Scope = ({ projectType, setProjectType, size, setSize, budget, setBudget, hours, setHours, timeline, setTimeline, rush, setRush, setStep }) => {
  const canNext = projectType && (size || budget || hours || timeline);
  return (
    <div data-testid="quote-step-2-content">
      <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 2 of 4</div>
      <h3 className="text-2xl md:text-3xl font-black">Project scope &amp; budget</h3>
      <p className="text-gray-400 text-sm mt-2">Help us size the work. All fields except project type are optional — we can estimate for you.</p>

      <div className="mt-6">
        <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Project Type <span className="text-orange-400">*</span></label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PROJECT_TYPES.map(p => {
            const Ic = Icons[p.icon];
            const active = projectType === p.val;
            return (
              <button key={p.val} type="button" onClick={() => setProjectType(p.val)}
                className={`flex items-center justify-center gap-2 px-3 py-3 rounded border text-xs font-bold uppercase tracking-wider transition-colors ${active ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-900/40 text-gray-400 hover:border-green-700'}`}
                data-testid={`quote-ptype-${p.val}`}>
                <Ic className="w-3.5 h-3.5" /> {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <PillGroup label="Budget Range"    options={BUDGET_RANGES} value={budget} onChange={setBudget} testidPrefix="quote-budget" />
        <PillGroup label="Estimated Hours" options={HOUR_RANGES}   value={hours}  onChange={setHours}  testidPrefix="quote-hours" />
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <PillGroup label="Timeline / ETA" options={TIMELINES} value={timeline} onChange={setTimeline} testidPrefix="quote-eta" />
        <div>
          <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Project Size</label>
          <div className="mt-2 grid grid-cols-1 gap-1.5">
            {['Small', 'Medium', 'Large', 'Enterprise'].map(p => (
              <button key={p} type="button" onClick={() => setSize(p)}
                className={`text-left px-3 py-2 rounded text-xs border transition-colors ${size === p ? 'bg-gray-900/40 border-gray-500 text-green-200' : 'border-gray-900/40 text-gray-400 hover:border-green-700'}`}
                data-testid={`quote-size-${p.toLowerCase()}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="mt-6 flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={rush} onChange={(e) => setRush(e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="quote-rush" />
        <span className="text-sm">
          <span className="font-bold text-orange-400">Rush delivery</span>
          <span className="text-gray-400 ml-2 text-xs">— prioritize over standard queue (additional fee may apply)</span>
        </span>
      </label>

      <div className="flex items-center justify-between mt-7">
        <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-gray-400 hover:text-green-400 text-xs font-bold uppercase tracking-wider"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back</button>
        <button onClick={() => setStep(3)} disabled={!canNext} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider" data-testid="quote-next-2">
          Next <Icons.ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

/* -------------------------- Step 3: Delivery -------------------------- */

const Step3Delivery = ({ delivery, setDelivery, reviews, setReviews, pref, setPref, nda, setNda, setStep }) => (
  <div data-testid="quote-step-3-content">
    <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 3 of 4</div>
    <h3 className="text-2xl md:text-3xl font-black">Delivery &amp; review preferences</h3>
    <p className="text-gray-400 text-sm mt-2">How should we deliver the work and how many review rounds do you expect?</p>

    <div className="mt-6">
      <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Preferred Delivery Format(s)</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {DELIVERY_FORMATS.map(d => (
          <SubServiceChip key={d.val} id={d.val} label={d.label} active={delivery.includes(d.val)} onClick={() => toggle(delivery, setDelivery, d.val)} />
        ))}
      </div>
    </div>

    <div className="mt-6">
      <PillGroup label="Review Rounds Expected" options={REVIEW_ROUNDS} value={reviews} onChange={setReviews} testidPrefix="quote-reviews" />
    </div>

    <div className="mt-6">
      <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Discuss this project via</label>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {CONTACT_PREFS.map(p => {
          const Ic = Icons[p.icon];
          const active = pref === p.val;
          return (
            <button key={p.val} type="button" onClick={() => setPref(p.val)}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded border text-xs font-bold uppercase tracking-wider transition-colors ${active ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-900/40 text-gray-400 hover:border-green-700'}`}
              data-testid={`quote-pref-${p.val}`}>
              <Ic className="w-3.5 h-3.5" /> {p.label}
            </button>
          );
        })}
      </div>
    </div>

    <label className="mt-6 flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={nda} onChange={(e) => setNda(e.target.checked)} className="w-4 h-4 accent-orange-500" data-testid="quote-nda" />
      <span className="text-sm"><span className="font-bold text-green-400">NDA required</span> <span className="text-gray-400 ml-2 text-xs">— we&apos;ll send a mutual NDA before sharing details</span></span>
    </label>

    <div className="flex items-center justify-between mt-7">
      <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-gray-400 hover:text-green-400 text-xs font-bold uppercase tracking-wider"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back</button>
      <button onClick={() => setStep(4)} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider" data-testid="quote-next-3">
        Next <Icons.ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

/* -------------------------- Step 4: Contact + submit -------------------------- */

const QUOTE_FIELDS = [
  ['firstName', 'First Name', true,  'text'],
  ['lastName',  'Last Name',  false, 'text'],
  ['email',     'Email Address', true,  'email'],
  ['phone',     'Phone',         false, 'tel'],
  ['company',   'Company',       false, 'text'],
  ['website',   'Website',       false, 'text']
];

const SummaryRow = ({ k, v }) => (
  <div><span className="text-gray-400">{k}:</span> <span className="text-green-300">{v}</span></div>
);

const Step4Contact = ({ summary, errMsg, loading, onBack, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="quote-step-4-content">
    <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 4 of 4</div>
    <h3 className="text-2xl md:text-3xl font-black">Your information</h3>
    <p className="text-gray-400 text-sm mt-2">We&apos;ll respond within 24 hours with a detailed quote.</p>

    <div className="mt-6 grid sm:grid-cols-2 gap-4">
      {QUOTE_FIELDS.map(([n, l, req, ty]) => (
        <div key={n}>
          <label className="text-xs uppercase tracking-wider text-green-400 font-bold">{l}{req && ' *'}</label>
          <input name={n} type={ty} required={req} className="mt-2 w-full bg-[#1a1f2e] border border-gray-900/50 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" data-testid={`quote-field-${n}`} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Project Description *</label>
        <textarea name="description" required rows={4} className="mt-2 w-full bg-[#1a1f2e] border border-gray-900/50 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" placeholder="Briefly describe deliverables, locations, deadlines, deliverable counts…" data-testid="quote-field-description"></textarea>
      </div>
    </div>

    <div className="mt-6 p-4 bg-[#1a1f2e] border border-gray-900/40 rounded text-xs grid sm:grid-cols-2 gap-2" data-testid="quote-summary">
      <div className="text-orange-400 font-bold uppercase tracking-wider mb-1 col-span-full">Your selections</div>
      <SummaryRow k="Services"     v={summary.services} />
      <SummaryRow k="Sub-services" v={summary.subs} />
      <SummaryRow k="Type"         v={summary.projectType || '—'} />
      <SummaryRow k="Size"         v={summary.size || '—'} />
      <SummaryRow k="Budget"       v={summary.budget || '—'} />
      <SummaryRow k="Hours"        v={summary.hours || '—'} />
      <SummaryRow k="Timeline"     v={`${summary.timeline || '—'}${summary.rush ? ' · RUSH' : ''}`} />
      <SummaryRow k="Reviews"      v={summary.reviews || '—'} />
      <div className="col-span-full">
        <SummaryRow k="Delivery"   v={summary.delivery.length ? summary.delivery.join(', ') : '—'} />
      </div>
      <SummaryRow k="Contact via"  v={summary.pref} />
      <SummaryRow k="NDA"          v={summary.nda ? 'Yes' : 'No'} />
    </div>

    {errMsg && <div className="mt-4 text-red-400 text-xs bg-red-900/20 border border-red-700/40 rounded px-3 py-2 flex items-center gap-2"><Icons.AlertCircle className="w-4 h-4" /> {errMsg}</div>}

    <div className="flex items-center justify-between mt-6">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-2 text-gray-400 hover:text-green-400 text-xs font-bold uppercase tracking-wider"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back</button>
      <button type="submit" disabled={loading} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider" data-testid="quote-submit">
        {loading ? <><Icons.Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Get My Quote <Icons.Send className="w-4 h-4" /></>}
      </button>
    </div>
  </form>
);

/* -------------------------- Success card -------------------------- */

const SuccessCard = ({ servicesCount, subsCount, pref, onReset }) => (
  <div className="bg-[#0e1116] text-white rounded-lg p-10 text-center" data-testid="quote-success">
    <Icons.CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
    <h3 className="text-3xl font-black mt-5">Quote Request Received!</h3>
    <p className="text-gray-300 mt-3 max-w-md mx-auto">
      Thanks. Our team is reviewing your {servicesCount} service{servicesCount !== 1 && 's'} ·{' '}
      {subsCount} sub-service{subsCount !== 1 && 's'} request and will respond via{' '}
      <span className="text-orange-400 font-bold">{pref}</span> within <span className="text-green-400 font-bold">24 hours</span>.
    </p>
    <button onClick={onReset} className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider">
      Start a new quote
    </button>
  </div>
);

/* -------------------------- Main container -------------------------- */

const CustomQuoteForm = () => {
  const { region } = useRegion();
  const { lang } = useLang();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [projectType, setProjectType] = useState('');
  const [size, setSize] = useState('');
  const [budget, setBudget] = useState('');
  const [hours, setHours] = useState('');
  const [timeline, setTimeline] = useState('');
  const [rush, setRush] = useState(false);
  const [delivery, setDelivery] = useState([]);
  const [reviews, setReviews] = useState('');
  const [pref, setPref] = useState('email');
  const [nda, setNda] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [done, setDone] = useState(false);

  const resetAll = () => {
    setDone(false); setStep(1); setServices([]); setSubs([]); setProjectType('');
    setSize(''); setBudget(''); setHours(''); setTimeline(''); setRush(false);
    setDelivery([]); setReviews(''); setPref('email'); setNda(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErrMsg('');
    const f = new FormData(e.target);
    try {
      await api.quote({
        lead_source: getLeadSource(),
        name: `${f.get('firstName') || ''} ${f.get('lastName') || ''}`.trim(),
        email: f.get('email') || '',
        phone: f.get('phone') || '',
        company: f.get('company') || '',
        website: f.get('website') || '',
        service: services.join(', '),
        description: f.get('description') || '',
        region: region?.code || 'global',
        language: lang,
        selected_services: services,
        selected_sub_services: subs,
        project_type: projectType,
        project_size: size,
        budget_range: budget,
        estimated_hours: hours,
        timeline,
        rush,
        delivery_format: delivery,
        review_rounds: reviews,
        discuss_preference: pref,
        nda_required: nda
      });
      setDone(true);
      e.target.reset();
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return <SuccessCard servicesCount={services.length} subsCount={subs.length} pref={pref} onReset={resetAll} />;
  }

  return (
    <div className="bg-[#0e1116] text-white rounded-lg p-6 md:p-10" data-testid="custom-quote-form">
      <ProgressBar step={step} setStep={setStep} />
      {step === 1 && (
        <Step1Services services={services} setServices={setServices} subs={subs} setSubs={setSubs} setStep={setStep} />
      )}
      {step === 2 && (
        <Step2Scope projectType={projectType} setProjectType={setProjectType} size={size} setSize={setSize}
          budget={budget} setBudget={setBudget} hours={hours} setHours={setHours}
          timeline={timeline} setTimeline={setTimeline} rush={rush} setRush={setRush} setStep={setStep} />
      )}
      {step === 3 && (
        <Step3Delivery delivery={delivery} setDelivery={setDelivery} reviews={reviews} setReviews={setReviews}
          pref={pref} setPref={setPref} nda={nda} setNda={setNda} setStep={setStep} />
      )}
      {step === 4 && (
        <Step4Contact
          summary={{ services: services.length, subs: subs.length, projectType, size, budget, hours, timeline, rush, reviews, delivery, pref, nda }}
          errMsg={errMsg} loading={loading}
          onBack={() => setStep(3)}
          onSubmit={submit}
        />
      )}
    </div>
  );
};

export default CustomQuoteForm;
